import * as React from 'react';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import { generateOpportunityDescription } from '../services/apiService';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import type { Salary, Opportunity, User, UserRole, AvailabilityStatus, Band } from '../types';
import { PUBLIC_LANDING_TYPES } from '../types';
import SparklesIcon from './icons/SparklesIcon';
import UserMultiSelect from './UserMultiSelect';
import BriefcaseIcon from './icons/BriefcaseIcon';
import CheckIcon from './icons/CheckIcon';

const { useState, useEffect, useRef } = React;

const opportunityTypes: Opportunity['type'][] = ['Job', 'Gig', 'Event', 'Jam Session', 'DJ Needed', 'Musician Needed', 'Audition', 'Showcase', 'Live Show', 'Stage Play', 'Movie', 'Short Movie', 'TV Show', 'Open Mic', 'DJ set', 'Stand Up Comedy', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

const CreateOpportunity: React.FC = () => {
  const { currentUser, users } = useAuth();
  const { createOpportunity, updateOpportunity } = useOpportunities();
  const { view, viewingOpportunity, handleNavigation, bands } = useNavigation();

  const isEditMode = view === 'editOpportunity' && viewingOpportunity;
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [type, setType] = useState<Opportunity['type']>('Job');
  const [instruments, setInstruments] = useState<string[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [roleNeeded, setRoleNeeded] = useState<UserRole | ''>('');
  const [visibility, setVisibility] = useState<Opportunity['visibility']>('public');
  const [showOnLandingPage, setShowOnLandingPage] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState<User[]>([]);
  const [isOpenCall, setIsOpenCall] = useState(true);
  const [invitedBands, setInvitedBands] = useState<Band[]>([]);
  const [bandSearch, setBandSearch] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Salary['currency']>('$');
  const [period, setPeriod] = useState<Salary['period']>('year');

  const [aiKeywords, setAiKeywords] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  useEffect(() => {
    const initMaps = async () => {
        const API_KEY = (window as any).GOOGLE_MAPS_API_KEY || "PASTE_YOUR_GOOGLE_MAPS_API_KEY_HERE";
        try {
            await loadGoogleMaps(API_KEY);
            if (!(window as any).google || !addressInputRef.current || autocompleteRef.current) return;

            autocompleteRef.current = new (window as any).google.maps.places.Autocomplete(addressInputRef.current, {
                fields: ['formatted_address', 'geometry', 'name', 'address_components']
            });

            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                if (place.formatted_address) {
                    const displayAddress = place.name && !place.formatted_address.includes(place.name) 
                      ? `${place.name}, ${place.formatted_address}` 
                      : place.formatted_address;
                    
                    setAddress(displayAddress);
                    
                    let city = '';
                    let reg = '';
                    let cntry = '';

                    if (place.address_components) {
                        for (const component of place.address_components) {
                            const types = component.types;
                            if (types.includes('locality') || types.includes('sublocality')) city = component.long_name;
                            if (types.includes('administrative_area_level_1')) reg = component.long_name;
                            if (types.includes('country')) cntry = component.long_name;
                        }
                    }
                    
                    setLocation(city || place.name || '');
                    setRegion(reg);
                    setCountry(cntry);

                    if (place.geometry?.location) {
                        setCoordinates({
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng()
                        });
                    }
                }
            });
        } catch (err) {
            console.error("Maps Init Error", err);
        }
    };
    initMaps();
  }, []);

  useEffect(() => {
    if (isEditMode && viewingOpportunity) {
      setTitle(viewingOpportunity.title);
      setDescription(viewingOpportunity.description);
      setLocation(viewingOpportunity.location);
      setRegion(viewingOpportunity.region || '');
      setCountry(viewingOpportunity.country || '');
      setAddress(viewingOpportunity.address || '');
      setCoordinates(viewingOpportunity.coordinates || null);
      setType(viewingOpportunity.type);
      setInstruments(viewingOpportunity.instrument?.split(',').map(s => s.trim()).filter(Boolean) || []);
      setGenres(viewingOpportunity.genre?.split(',').map(s => s.trim()).filter(Boolean) || []);
      setRoleNeeded((viewingOpportunity.roleNeeded as UserRole | '') || '');
      setVisibility(viewingOpportunity.visibility || 'public');
      setShowOnLandingPage(viewingOpportunity.showOnLandingPage || false);
      setIsOpenCall(viewingOpportunity.isOpenCall !== false);
      setMaxCapacity(viewingOpportunity.maxCapacity ? String(viewingOpportunity.maxCapacity) : '');
      setCoverImage(viewingOpportunity.imageUrl || null);
      setInvitedBands(bands.filter(b => (viewingOpportunity.invitedBands || []).includes(b.id)));
      setStartDate(viewingOpportunity.startDate?.split('T')[0] || '');
      setEndDate(viewingOpportunity.endDate?.split('T')[0] || '');
      setStartTime(viewingOpportunity.startTime || '');
      setEndTime(viewingOpportunity.endTime || '');
      setTimezone(viewingOpportunity.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      if (viewingOpportunity.salary) {
        setCurrency(viewingOpportunity.salary.currency);
        setAmount(String(viewingOpportunity.salary.amount));
        setPeriod(viewingOpportunity.salary.period);
      }
    }
  }, [isEditMode, viewingOpportunity, bands]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setCoverImage(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleGenerateDescription = async () => {
    if (!aiKeywords.trim() || !title.trim()) return;
    setIsGenerating(true);
    try {
        const generatedDescription = await generateOpportunityDescription(title, type, aiKeywords);
        setDescription(generatedDescription);
    } catch (error) {
        console.error('Failed to generate description.', error);
    } finally {
        setIsGenerating(false);
    }
  };

  const getArtistAvailabilityForDate = (artist: User, date: string): AvailabilityStatus => {
      if (!date || !artist.availability) return 'neutral';
      return artist.availability[date] || 'neutral';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !address.trim() || !location) {
        alert("Please ensure location is selected from the dropdown to verify City/Region/Country.");
        return;
    }

    const salaryAmount = parseFloat(amount);
    const salaryData = !isNaN(salaryAmount) && salaryAmount > 0 ? { currency, amount: salaryAmount, period } : undefined;

    const opportunityData = {
      title, description, location, region, country, address, coordinates: coordinates || undefined, type,
      instrument: instruments.join(', ') || undefined,
      genre: genres.join(', ') || undefined,
      roleNeeded: roleNeeded || undefined,
      visibility,
      showOnLandingPage: visibility === 'public' ? showOnLandingPage : false,
      isOpenCall,
      invitedUsers: (!isOpenCall || visibility === 'private') ? invitedUsers.map(u => u.handle) : undefined,
      invitedBands: !isOpenCall ? invitedBands.map(b => b.id) : undefined,
      maxCapacity: maxCapacity ? parseInt(maxCapacity) : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      timezone: timezone || undefined,
      salary: salaryData,
      imageUrl: coverImage || undefined,
    };

    if (isEditMode && viewingOpportunity) {
      await updateOpportunity(viewingOpportunity.id, opportunityData);
    } else {
      await createOpportunity(opportunityData);
    }
    handleNavigation('feed');
  };

  return (
    <div className="bg-brand-surface rounded-lg p-6 font-urbanist animate-in fade-in duration-500">
      <h2 className="text-2xl font-black text-white mb-6 border-b border-gray-700 pb-4 tracking-tighter uppercase">{isEditMode ? 'Edit Opportunity' : 'Create a New Opportunity'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Title</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Lead Guitarist for Touring Band" className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold" required />
        </div>
        
        <div className={`bg-gray-800/50 rounded-lg p-4 space-y-3 border border-gray-700/50 ${!currentUser?.subscriptionTier?.includes('pro_plus') ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <SparklesIcon className="h-5 w-5 text-brand-orange" />
                    <h3 className="text-lg font-bold text-white tracking-tight uppercase">AI Copywriter</h3>
                </div>
            </div>
            <p className="text-sm text-brand-muted">Describe your need in a few words and let AI polish it.</p>
            <input type="text" value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} placeholder="e.g., Jazz club, weekly gig, pro only" className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange" />
            <button type="button" onClick={handleGenerateDescription} disabled={isGenerating || !aiKeywords.trim()} className="w-full bg-brand-orange/20 text-brand-orange border border-brand-orange font-bold py-2 rounded-full hover:bg-brand-orange/30 disabled:opacity-50 transition-all tracking-widest text-[10px] uppercase">
                {isGenerating ? 'Drafting...' : 'Generate Description'}
            </button>
        </div>

        <div><label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Description</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the role..." className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none leading-relaxed font-bold" rows={6} required/></div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Visibility</label>
                <div className="flex bg-gray-700 rounded-full p-1">
                    <button type="button" onClick={() => setVisibility('public')} className={`w-1/2 py-2 rounded-full font-black tracking-widest text-[10px] uppercase transition-colors ${visibility === 'public' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:bg-gray-600'}`}>Public</button>
                    <button type="button" onClick={() => setVisibility('private')} className={`w-1/2 py-2 rounded-full font-black tracking-widest text-[10px] uppercase transition-colors ${visibility === 'private' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:bg-gray-600'}`}>Private</button>
                </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value as any)} className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange appearance-none font-bold">
                  {opportunityTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
        </div>

        {visibility === 'public' && PUBLIC_LANDING_TYPES.includes(type) && (
            <div className={`flex items-center justify-between rounded-xl px-4 py-3 border transition-colors ${showOnLandingPage ? 'bg-brand-orange/10 border-brand-orange/30' : 'bg-gray-800 border-gray-700'}`}>
                <div>
                    <p className="text-sm font-black text-white uppercase tracking-tight">Show on public landing page</p>
                    <p className="text-[11px] text-brand-muted mt-0.5">Non-registered users will discover this event at the top of the site</p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowOnLandingPage(v => !v)}
                    className={`relative flex-shrink-0 w-12 h-6 rounded-full overflow-hidden transition-colors ${showOnLandingPage ? 'bg-brand-orange' : 'bg-gray-600'}`}
                    aria-label="Toggle public landing page"
                >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${showOnLandingPage ? 'right-1' : 'left-1'}`} />
                </button>
            </div>
        )}

        <div>
            <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Open Call</label>
            <div className="flex bg-gray-700 rounded-full p-1">
                <button type="button" onClick={() => setIsOpenCall(true)} className={`w-1/2 py-2 rounded-full font-black tracking-widest text-[10px] uppercase transition-colors ${isOpenCall ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:bg-gray-600'}`}>Yes — Open to All</button>
                <button type="button" onClick={() => setIsOpenCall(false)} className={`w-1/2 py-2 rounded-full font-black tracking-widest text-[10px] uppercase transition-colors ${!isOpenCall ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:bg-gray-600'}`}>No — Invite Only</button>
            </div>
        </div>

        {(!isOpenCall || visibility === 'private') && (
            <div className="bg-gray-800/40 p-4 rounded-xl border border-gray-700 space-y-4">
                <UserMultiSelect
                    label="Invite Artists"
                    placeholder="Search and select artists to invite..."
                    availableUsers={users.filter(u => u.handle !== currentUser?.handle)}
                    selectedUsers={invitedUsers}
                    onSelectionChange={setInvitedUsers}
                />

                {invitedUsers.length > 0 && startDate && (
                    <div className="space-y-2">
                        <label className="block text-[10px] font-black text-brand-orange tracking-widest uppercase">Invitee Availability for {startDate}</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {invitedUsers.map(user => {
                                const status = getArtistAvailabilityForDate(user, startDate);
                                return (
                                    <div key={user.handle} className="flex items-center justify-between bg-gray-900/60 p-2 rounded-lg border border-gray-700">
                                        <div className="flex items-center gap-2">
                                            <img src={user.avatarUrl} className="h-6 w-6 rounded-full" alt=""/>
                                            <span className="text-xs font-bold text-white uppercase">{user.name}</span>
                                        </div>
                                        {status === 'available' && <span className="text-[9px] font-black text-green-400 flex items-center gap-1">Available</span>}
                                        {status === 'busy' && <span className="text-[9px] font-black text-orange-400 flex items-center gap-1"><BriefcaseIcon className="h-3 w-3"/> Busy</span>}
                                        {status === 'day_off' && <span className="text-[9px] font-black text-red-400 flex items-center gap-1">Off</span>}
                                        {status === 'neutral' && <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Unknown</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!isOpenCall && (
                    <div>
                        <label className="block text-[10px] font-black text-brand-muted mb-2 tracking-widest uppercase">Invite Projects / Bands</label>
                        <input
                            type="text" value={bandSearch} onChange={e => setBandSearch(e.target.value)}
                            placeholder="Search projects..."
                            className="w-full bg-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange mb-2 font-bold"
                        />
                        <div className="max-h-40 overflow-y-auto space-y-1">
                            {bands
                                .filter(b => b.name.toLowerCase().includes(bandSearch.toLowerCase()) && !invitedBands.find(ib => ib.id === b.id))
                                .slice(0, 8)
                                .map(b => (
                                    <button key={b.id} type="button" onClick={() => { setInvitedBands(prev => [...prev, b]); setBandSearch(''); }}
                                        className="w-full flex items-center gap-2 p-2 rounded-lg bg-gray-900/60 hover:bg-gray-900 text-left">
                                        <img src={b.avatarUrl} className="h-6 w-6 rounded object-cover" alt=""/>
                                        <span className="text-xs font-bold text-white">{b.name}</span>
                                    </button>
                                ))
                            }
                        </div>
                        {invitedBands.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {invitedBands.map(b => (
                                    <span key={b.id} className="flex items-center gap-1 bg-brand-orange/20 text-brand-orange text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest">
                                        {b.name}
                                        <button type="button" onClick={() => setInvitedBands(prev => prev.filter(x => x.id !== b.id))} className="ml-1 text-brand-orange/60 hover:text-brand-orange">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )}

        <div>
            <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Location / Venue</label>
            <div className="relative">
                <input 
                    ref={addressInputRef}
                    type="text" 
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)} 
                    placeholder="Search for a city or venue..." 
                    className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold pr-10"
                    required
                />
                {location && (
                    <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-400" title="Location Verified" />
                )}
            </div>
            {location && (
                <div className="flex flex-wrap items-center gap-2 mt-2 bg-gray-800/30 p-2 rounded-lg border border-gray-700">
                    <span className="text-[9px] font-black text-brand-muted uppercase tracking-widest">Index:</span>
                    <span className="text-[9px] bg-brand-orange/10 text-brand-orange font-black px-2 py-0.5 rounded uppercase tracking-widest">{location}</span>
                    {region && <span className="text-[9px] bg-blue-500/10 text-blue-400 font-black px-2 py-0.5 rounded uppercase tracking-widest">{region}</span>}
                    {country && <span className="text-[9px] bg-green-500/10 text-green-400 font-black px-2 py-0.5 rounded uppercase tracking-widest">{country}</span>}
                </div>
            )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Start Date</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold"/></div>
            <div><label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">End Date</label><input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold"/></div>
        </div>

        <div>
            <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Max Participants (Optional)</label>
            <input type="number" min="1" value={maxCapacity} onChange={e => setMaxCapacity(e.target.value)} placeholder="Leave empty for unlimited" className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold" />
        </div>

         <div><label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Salary / Rate (Optional)</label><div className="flex items-center gap-2"><select value={currency} onChange={e => setCurrency(e.target.value as any)} className="bg-gray-700 text-white rounded-md p-3 outline-none font-bold"><option>$</option><option>€</option><option>£</option></select><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 500" className="flex-grow w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold"/><select value={period} onChange={e => setPeriod(e.target.value as any)} className="bg-gray-700 text-white rounded-md p-3 outline-none font-bold"><option value="year">per year</option><option value="day">per day</option><option value="hour">per hour</option><option value="flat rate">flat rate</option></select></div></div>
        <div>
          <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Cover Image (optional, max 5MB)</label>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} className="hidden" />
          {coverImage ? (
            <div className="relative">
              <img src={coverImage} alt="cover preview" className="w-full h-48 object-cover rounded-lg" />
              <button type="button" onClick={() => setCoverImage(null)} className="absolute top-2 right-2 bg-black/70 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-black text-lg leading-none">×</button>
            </div>
          ) : (
            <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full h-24 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-brand-muted hover:border-brand-orange hover:text-brand-orange transition-colors">
              <span className="text-[10px] font-black uppercase tracking-widest">+ Add Cover Image</span>
            </button>
          )}
        </div>
        <div className="flex justify-end pt-4"><button type="submit" className="bg-brand-orange text-white font-black px-10 py-4 rounded-full hover:bg-orange-600 shadow-lg tracking-widest text-sm uppercase">Post</button></div>
      </form>
    </div>
  );
};

export default CreateOpportunity;